import React, { useState, useEffect } from 'react';
import { MessageSquare, Send, Sparkles, Code, TrendingUp, AlertCircle, BarChart3, Lightbulb, Target, MapPin, Award, RefreshCw } from 'lucide-react';
import { ScatterPlot, DistributionHistogram, ComparisonBarChart, AthleteRadarChart } from '../charts';
import CorrelationInsightsPanel from './CorrelationInsightsPanel';
import AthleteDetailModal from './AthleteDetailModal';
import { detectVisualizationType, suggestFollowUpQueries, shouldShowCorrelationInsights } from '../../utils/visualizationEngine';

export default function AIInsights() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentResult, setCurrentResult] = useState(null);
  const [visualizationType, setVisualizationType] = useState(null);
  const [showCorrelations, setShowCorrelations] = useState(false);
  const [followUpSuggestions, setFollowUpSuggestions] = useState([]);
  const [smartInsights, setSmartInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('chat'); // 'chat' or 'insights'
  const [rightPanelTab, setRightPanelTab] = useState('table'); // 'visualization', 'correlation', 'table' - default to table
  const [selectedAthlete, setSelectedAthlete] = useState(null); // For athlete detail modal
  const [loadingAthlete, setLoadingAthlete] = useState(false); // Loading state for athlete fetch
  const [selectedModel, setSelectedModel] = useState('auto'); // 'auto', 'openai', 'groq', 'gemini'

  // Load smart insights on mount
  useEffect(() => {
    loadSmartInsights();
  }, []);

  const loadSmartInsights = async () => {
    setInsightsLoading(true);
    try {
      const response = await fetch('/api/analytics-insights', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'smart-insights' })
      });
      const result = await response.json();
      if (result.success) {
        setSmartInsights(result.insights);
      }
    } catch (error) {
      console.error('Failed to load smart insights:', error);
    } finally {
      setInsightsLoading(false);
    }
  };

  // Fetch full athlete profile from database
  const fetchAthleteProfile = async (athleteId) => {
    setLoadingAthlete(true);
    try {
      const response = await fetch('/api', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'api.execute',
          data: {
            actionKey: 'athletes.get',
            params: { athlete_id: athleteId },
            user: { role: 'admin' }
          }
        })
      });

      const result = await response.json();
      
      if (result.success && result.data && result.data.length > 0) {
        setSelectedAthlete(result.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch athlete profile:', error);
    } finally {
      setLoadingAthlete(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = { role: 'user', content: input };
    setMessages([...messages, userMessage]);
    setInput('');
    setLoading(true);

    try {
      // Call AI Insights API
      const response = await fetch('/api/ai-insights', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: input,
          conversationHistory: messages.slice(-4), // Last 2 exchanges for context
          selectedModel: selectedModel // Pass selected model to API
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to process query');
      }

      // Add AI response to messages
      const aiMessage = {
        role: 'assistant',
        content: `Found ${result.rowCount} results in ${result.executionTime}ms`,
        sql: result.sql,
        data: result.data,
        chartSuggestion: result.chartSuggestion,
        columns: result.columns
      };

      setMessages(prev => [...prev, aiMessage]);
      setCurrentResult(aiMessage);

      // Auto-detect visualization type with validation
      if (result.data && result.data.length > 0 && result.columns && result.columns.length > 0) {
        const vizType = detectVisualizationType(result.data, result.sql, result.columns);
        setVisualizationType(vizType);

        // Check if correlation insights should be shown
        const showCorr = shouldShowCorrelationInsights(result.data, result.columns);
        setShowCorrelations(showCorr);

        // Generate follow-up suggestions
        const suggestions = suggestFollowUpQueries(result.data, result.sql, result.columns);
        setFollowUpSuggestions(suggestions);
        
        // Always default to table tab - let user explore visualization manually
        setRightPanelTab('table');
      } else {
        // No valid data, show table tab
        setVisualizationType(null);
        setShowCorrelations(false);
        setFollowUpSuggestions([]);
        setRightPanelTab('table');
      }

    } catch (error) {
      console.error('AI Insights Error:', error);
      
      let errorContent = 'Sorry, I encountered an error processing your query.';
      
      if (error.message?.includes('quota')) {
        errorContent = '⚠️ AI service quota exceeded. This feature requires Vercel Pro plan credits. Please check your Vercel deployment settings.';
      } else if (error.message?.includes('configuration')) {
        errorContent = '⚠️ AI service not configured. This feature works on Vercel deployment with Pro plan.';
      } else if (error.message?.includes('Failed to fetch')) {
        errorContent = '⚠️ Could not connect to AI service. Please try again.';
      }
      
      const errorMessage = {
        role: 'assistant',
        content: errorContent,
        error: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <div className="grid grid-cols-12 gap-6 h-[calc(100vh-180px)]">
      {/* LEFT PANEL - Chat Interface (40%) */}
      <div className="col-span-5 bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6 flex flex-col">
        {/* Tab Switcher */}
        <div className="flex space-x-2 mb-6">
          <button
            onClick={() => setActiveTab('chat')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
              activeTab === 'chat'
                ? 'bg-purple-600 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            <Sparkles className="w-4 h-4" />
            <span>AI Chat</span>
          </button>
          <button
            onClick={() => setActiveTab('insights')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
              activeTab === 'insights'
                ? 'bg-purple-600 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            <Lightbulb className="w-4 h-4" />
            <span>Smart Insights</span>
          </button>
        </div>

        {activeTab === 'chat' ? (
          <>
            <div className="flex items-center space-x-2 mb-4">
              <Sparkles className="w-5 h-5 text-purple-400" />
              <h2 className="text-xl font-bold text-white">AI Assistant</h2>
            </div>

        {/* Chat History */}
        <div className="flex-1 overflow-y-auto space-y-4 mb-4">
          {messages.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400 mb-2">Ask me anything about the athlete data</p>
              <p className="text-slate-500 text-sm">Try: "Show me tall athletes from Patna"</p>
            </div>
          ) : (
            messages.map((msg, idx) => (
              <div
                key={idx}
                className={`p-4 rounded-lg ${
                  msg.role === 'user'
                    ? 'bg-blue-600/20 border border-blue-500/30 ml-8'
                    : 'bg-slate-700/50 border border-slate-600 mr-8'
                }`}
              >
                <div className="flex items-start space-x-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    msg.role === 'user' ? 'bg-blue-600' : 'bg-purple-600'
                  }`}>
                    {msg.role === 'user' ? '👤' : '🤖'}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-white">{msg.content}</p>
                    {msg.sql && (
                      <div className="mt-2 p-2 bg-slate-900/50 rounded border border-slate-600">
                        <div className="flex items-center space-x-2 mb-1">
                          <Code className="w-3 h-3 text-green-400" />
                          <span className="text-xs text-slate-400">Generated SQL</span>
                        </div>
                        <code className="text-xs text-green-300 font-mono">{msg.sql}</code>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
          {loading && (
            <div className="flex items-center space-x-2 text-slate-400">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-400"></div>
              <span className="text-sm">AI is thinking...</span>
            </div>
          )}
        </div>

        {/* Model Selector & Input */}
        <div className="mb-3 flex items-center space-x-2">
          <label className="text-xs text-slate-400">AI Model:</label>
          <select
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            className="px-3 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="auto">Auto (OpenAI → Groq)</option>
            <option value="openai">OpenAI GPT-4</option>
            <option value="groq">Groq Llama 3.3</option>
            <option value="gemini">Google Gemini</option>
          </select>
          <span className="text-xs text-slate-500">• Test different models</span>
        </div>
        
        <div className="flex space-x-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Ask about athlete data..."
            className="flex-1 px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          <button
            onClick={handleSend}
            disabled={loading || !input.trim()}
            className="px-4 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
          </>
        ) : (
          // Smart Insights Panel
          <div className="flex-1 overflow-y-auto space-y-4">
            {insightsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400"></div>
              </div>
            ) : smartInsights ? (
              <>
                {/* Key Findings */}
                {smartInsights.keyFindings?.length > 0 && (
                  <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                    <div className="flex items-center space-x-2 mb-3">
                      <Target className="w-4 h-4 text-blue-400" />
                      <h3 className="text-sm font-semibold text-white">Key Findings</h3>
                    </div>
                    <div className="space-y-2">
                      {smartInsights.keyFindings.map((finding, idx) => (
                        <div key={idx} className="text-sm text-slate-300 bg-slate-800/50 p-3 rounded">
                          {finding.message}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Talent Pools */}
                {smartInsights.talentPools?.length > 0 && (
                  <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                    <div className="flex items-center space-x-2 mb-3">
                      <MapPin className="w-4 h-4 text-green-400" />
                      <h3 className="text-sm font-semibold text-white">Talent Hotspots</h3>
                    </div>
                    <div className="space-y-2">
                      {smartInsights.talentPools.map((pool, idx) => (
                        <div key={idx} className="text-sm text-slate-300 bg-slate-800/50 p-3 rounded">
                          {pool.message}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Growth Opportunities */}
                {smartInsights.growthOpportunities?.length > 0 && (
                  <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                    <div className="flex items-center space-x-2 mb-3">
                      <TrendingUp className="w-4 h-4 text-purple-400" />
                      <h3 className="text-sm font-semibold text-white">Growth Opportunities</h3>
                    </div>
                    <div className="space-y-2">
                      {smartInsights.growthOpportunities.map((opp, idx) => (
                        <div key={idx} className="text-sm text-slate-300 bg-slate-800/50 p-3 rounded">
                          {opp.message}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recommendations */}
                {smartInsights.recommendations?.length > 0 && (
                  <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                    <div className="flex items-center space-x-2 mb-3">
                      <Award className="w-4 h-4 text-yellow-400" />
                      <h3 className="text-sm font-semibold text-white">Recommendations</h3>
                    </div>
                    <div className="space-y-2">
                      {smartInsights.recommendations.map((rec, idx) => (
                        <div key={idx} className="text-sm text-slate-300 bg-slate-800/50 p-3 rounded flex items-start space-x-2">
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            rec.priority === 'high' ? 'bg-red-500/20 text-red-300' :
                            rec.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-300' :
                            'bg-blue-500/20 text-blue-300'
                          }`}>
                            {rec.priority}
                          </span>
                          <span className="flex-1">{rec.message}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={loadSmartInsights}
                  className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-all flex items-center justify-center space-x-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Refresh Insights</span>
                </button>
              </>
            ) : (
              <div className="text-center py-12 text-slate-400">
                No insights available
              </div>
            )}
          </div>
        )}
      </div>

      {/* RIGHT PANEL - Results with Tabs (60%) */}
      <div className="col-span-7 bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 flex flex-col h-full">
        {!currentResult ? (
          <div className="flex items-center justify-center h-full p-6">
            <div className="text-center">
              <TrendingUp className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">AI Insights</h3>
              <p className="text-slate-400 mb-4">
                Ask questions in natural language and get instant visualizations
              </p>
              <div className="bg-slate-700/50 rounded-lg p-4 max-w-md mx-auto text-left">
                <p className="text-sm text-slate-300 mb-2">Example queries:</p>
                <ul className="text-sm text-slate-400 space-y-1">
                  <li>• "Show me tall athletes from Patna"</li>
                  <li>• "Compare average height by gender"</li>
                  <li>• "Find correlation between height and vertical jump"</li>
                  <li>• "Top 10 athletes by vertical jump"</li>
                </ul>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Tab Navigation */}
            <div className="flex items-center justify-between border-b border-slate-700 px-6 py-3">
              <div className="flex space-x-2">
                {/* Data Table Tab - Always First */}
                <button
                  onClick={() => setRightPanelTab('table')}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
                    rightPanelTab === 'table'
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  <Code className="w-4 h-4" />
                  <span>Data Table</span>
                </button>
                
                {/* Visualization Tab - Only show if meaningful visualization available */}
                {visualizationType && visualizationType.type !== 'none' && visualizationType.type !== 'table' && (
                  <button
                    onClick={() => setRightPanelTab('visualization')}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
                      rightPanelTab === 'visualization'
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    <BarChart3 className="w-4 h-4" />
                    <span>Visualization</span>
                  </button>
                )}
                
                {/* Correlation Tab - Only show when applicable */}
                {showCorrelations && (
                  <button
                    onClick={() => setRightPanelTab('correlation')}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
                      rightPanelTab === 'correlation'
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    <TrendingUp className="w-4 h-4" />
                    <span>Correlation</span>
                  </button>
                )}
              </div>
              
              <div className="text-xs text-slate-400">
                {currentResult.data.length} rows • {currentResult.sql ? 'SQL executed' : 'Results'}
              </div>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Visualization Tab */}
              {rightPanelTab === 'visualization' && (
                <div className="space-y-6">
                  {visualizationType && visualizationType.type !== 'none' && visualizationType.type !== 'table' ? (
                    <>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm text-slate-400">{visualizationType.reason}</span>
                      </div>
                      
                      <div className="h-[500px]">
                        {visualizationType.type === 'scatter' && (
                          <ScatterPlot
                            data={currentResult.data}
                            xKey={visualizationType.config.xKey}
                            yKey={visualizationType.config.yKey}
                            xLabel={visualizationType.config.xLabel}
                            yLabel={visualizationType.config.yLabel}
                            title={`${visualizationType.config.xLabel} vs ${visualizationType.config.yLabel}`}
                          />
                        )}
                        
                        {visualizationType.type === 'histogram' && (
                          <DistributionHistogram
                            data={currentResult.data}
                            dataKey={visualizationType.config.dataKey}
                            xLabel={visualizationType.config.xLabel}
                            bins={visualizationType.config.bins}
                            showMean={true}
                            showMedian={false}
                          />
                        )}
                        
                        {visualizationType.type === 'bar' && (
                          <ComparisonBarChart
                            data={currentResult.data}
                            xKey={visualizationType.config.xKey}
                            dataKeys={visualizationType.config.dataKeys}
                            xLabel={visualizationType.config.xLabel}
                            yLabel={visualizationType.config.yLabel}
                            horizontal={visualizationType.config.horizontal}
                          />
                        )}
                        
                        {visualizationType.type === 'radar' && (
                          <AthleteRadarChart
                            athletes={visualizationType.config.athletes}
                            attributes={visualizationType.config.attributes}
                            title="Athlete Profile Comparison"
                          />
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-64 text-slate-400">
                      <div className="text-center">
                        <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No visualization available for this query</p>
                        <p className="text-sm mt-2">Try switching to the Data Table tab</p>
                      </div>
                    </div>
                  )}

                  {/* Follow-up Suggestions */}
                  {followUpSuggestions.length > 0 && (
                    <div className="bg-slate-700/30 rounded-lg p-4 border border-slate-700">
                      <div className="flex items-center space-x-2 mb-3">
                        <Sparkles className="w-4 h-4 text-purple-400" />
                        <h4 className="text-sm font-semibold text-white">Suggested Follow-ups</h4>
                      </div>
                      <div className="space-y-2">
                        {followUpSuggestions.map((suggestion, idx) => (
                          <button
                            key={idx}
                            onClick={() => setInput(suggestion.text)}
                            className="w-full text-left px-3 py-2 bg-slate-700/50 hover:bg-slate-600 rounded-lg transition-colors group"
                          >
                            <p className="text-sm text-slate-300 group-hover:text-white">{suggestion.text}</p>
                            <p className="text-xs text-slate-500 mt-1">{suggestion.hint}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Correlation Tab */}
              {rightPanelTab === 'correlation' && showCorrelations && (
                <CorrelationInsightsPanel
                  data={currentResult.data}
                  columns={currentResult.columns}
                />
              )}

              {/* Data Table Tab */}
              {rightPanelTab === 'table' && (
                <div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-700">
                          {currentResult.columns.map((col, idx) => (
                            <th key={idx} className="text-left py-2 px-3 text-slate-300 font-medium">
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {currentResult.data.slice(0, 50).map((row, idx) => {
                          // Check if row has athlete data (has name)
                          const hasAthleteName = row.full_name || row.first_name;
                          
                          // For AI results without ID, we need to fetch by name
                          // Since we don't have ID in the query results, disable clicking for now
                          const athleteId = row.athlete_id || row.id;
                          const canClick = hasAthleteName && athleteId;
                          
                          return (
                            <tr 
                              key={idx} 
                              onClick={(e) => {
                                if (canClick) {
                                  e.stopPropagation();
                                  fetchAthleteProfile(athleteId);
                                }
                              }}
                              className={`border-b border-slate-700/50 hover:bg-slate-700/30 ${
                                canClick ? 'cursor-pointer hover:bg-blue-900/20' : ''
                              }`}
                              title={canClick ? 'Click to view full athlete profile' : hasAthleteName ? 'Athlete ID not available in query results' : ''}
                            >
                              {currentResult.columns.map((col, colIdx) => (
                                <td key={colIdx} className="py-2 px-3 text-slate-400">
                                  {typeof row[col] === 'number' ? row[col].toFixed(2) : row[col]}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {currentResult.data.length > 50 && (
                      <p className="text-xs text-slate-500 mt-4 text-center">
                        Showing first 50 of {currentResult.data.length} results
                      </p>
                    )}
                  </div>
                  
                  {/* SQL Query Display */}
                  {currentResult.sql && (
                    <div className="mt-6 bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                      <div className="flex items-center space-x-2 mb-2">
                        <Code className="w-4 h-4 text-green-400" />
                        <h4 className="text-sm font-semibold text-slate-300">Generated SQL</h4>
                      </div>
                      <pre className="text-xs text-green-400 overflow-x-auto">
                        {currentResult.sql}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>

    {/* Athlete Detail Modal */}
    {(selectedAthlete || loadingAthlete) && (
      <AthleteDetailModal
        athlete={selectedAthlete}
        allAthletes={currentResult?.data || []}
        onClose={() => {
          setSelectedAthlete(null);
          setLoadingAthlete(false);
        }}
        loading={loadingAthlete}
      />
    )}
  </>
  );
}
